import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, ShieldCheck, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ghbb_admin_token";
const STATUSES = ["pending", "confirmed", "cancelled"];
const STATUS_STYLES = {
  pending: "bg-[#C98A2B]/15 text-[#8A5A10] border-[#C98A2B]/40",
  confirmed: "bg-[#4F7942]/15 text-[#38592F] border-[#4F7942]/40",
  cancelled: "bg-[#B0413E]/10 text-[#8F3431] border-[#B0413E]/40",
};

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBookings([]);
  }, []);

  const fetchBookings = useCallback(async (tk) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings`, { headers: authHeaders(tk) });
      setBookings(res.data);
      return true;
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin token");
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Failed to load bookings");
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchBookings(token);
  }, [token, fetchBookings]);

  const handleLogin = async (candidate) => {
    const ok = await fetchBookings(candidate);
    if (ok) {
      sessionStorage.setItem(TOKEN_KEY, candidate);
      setToken(candidate);
      toast.success("Signed in");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.patch(
        `${API}/bookings/${id}/status`,
        { status },
        { headers: authHeaders(token) }
      );
      setBookings((prev) => prev.map((b) => (b.id === id ? res.data : b)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        toast.error("Session expired, please sign in again");
      } else {
        toast.error("Update failed");
      }
    }
  };

  if (!token) return <AdminLogin onSubmit={handleLogin} loading={loading} />;

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const counts = STATUSES.reduce(
    (acc, s) => ({ ...acc, [s]: bookings.filter((b) => b.status === s).length }),
    { all: bookings.length }
  );

  return (
    <section className="py-12 md:py-16 min-h-[70vh]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="overline">Owner area</div>
            <h1 className="mt-3 text-4xl md:text-5xl leading-[1.05]">
              Booking requests
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => fetchBookings(token)}
              data-testid="admin-refresh"
              className="btn-outline py-3 px-5"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="btn-outline py-3 px-5"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-10 flex gap-2 flex-wrap" data-testid="admin-status-filters">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`admin-filter-${s}`}
              className={`text-xs tracking-[0.18em] uppercase px-4 py-2 border transition-colors ${
                filter === s
                  ? "bg-[#2C241B] text-[#F9F6F0] border-[#2C241B]"
                  : "border-[#6B4A2B]/25 text-[#5C5042] hover:border-[#6B4A2B]"
              }`}
            >
              {s} ({counts[s]})
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div
            className="mt-10 p-12 text-center bg-[#EFEAE1] text-[#5C5042]"
            data-testid="admin-empty-state"
          >
            {loading ? "Loading…" : "No booking requests in this view."}
          </div>
        ) : (
          <div className="mt-10 space-y-4">
            {filtered.map((b) => (
              <BookingRow key={b.id} booking={b} onUpdateStatus={updateStatus} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function AdminLogin({ onSubmit, loading }) {
  const [value, setValue] = useState("");

  return (
    <section className="py-24 md:py-32" data-testid="admin-login">
      <div className="max-w-md mx-auto px-5 md:px-8">
        <div className="mx-auto h-14 w-14 border border-[#6B4A2B]/40 text-[#6B4A2B] flex items-center justify-center">
          <ShieldCheck size={24} strokeWidth={1.3} />
        </div>
        <div className="mt-8 overline text-center">Owner area</div>
        <h1 className="mt-3 text-4xl text-center leading-[1.1]">Admin sign in</h1>
        <p className="mt-4 text-sm text-[#5C5042] text-center">
          Enter your admin token to view and manage booking requests.
        </p>
        <form
          className="mt-10"
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) onSubmit(value.trim());
          }}
        >
          <input
            type="password"
            required
            autoFocus
            data-testid="admin-token-input"
            className="paper-input"
            placeholder="Admin token"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-primary w-full justify-center mt-6"
          >
            {loading ? "Checking…" : "Sign in"} {!loading && <ArrowUpRight size={14} />}
          </button>
        </form>
      </div>
    </section>
  );
}

function BookingRow({ booking: b, onUpdateStatus }) {
  const created = b.created_at ? new Date(b.created_at).toLocaleString("en-GB") : "";

  return (
    <article
      className="bg-white border border-[#6B4A2B]/10 p-6 md:p-8 grid md:grid-cols-12 gap-6"
      data-testid={`admin-booking-${b.id}`}
    >
      <div className="md:col-span-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-serif-display text-2xl">
            {b.first_name} {b.last_name}
          </h2>
          <span
            data-testid={`admin-booking-status-${b.id}`}
            className={`text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 border ${STATUS_STYLES[b.status] || ""}`}
          >
            {b.status}
          </span>
        </div>
        <div className="mt-3 text-sm text-[#5C5042] space-y-1">
          <div>
            <a href={`mailto:${b.email}`} className="hover:text-[#6B4A2B] underline underline-offset-2">
              {b.email}
            </a>
          </div>
          <div>
            <a
              href={`https://wa.me/${b.phone.replace(/\D/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="hover:text-[#6B4A2B] underline underline-offset-2"
            >
              {b.phone}
            </a>
          </div>
          <div className="text-xs opacity-70">Received {created}</div>
        </div>
      </div>

      <div className="md:col-span-4 text-sm text-[#2C241B]">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Meta label="Room" value={b.room_id} />
          <Meta label="Guests" value={b.guests} />
          <Meta label="Check-in" value={b.check_in} />
          <Meta label="Check-out" value={b.check_out} />
        </div>
        {b.message && (
          <p className="mt-4 text-sm text-[#5C5042] italic border-l-2 border-[#6B4A2B]/30 pl-3">
            {b.message}
          </p>
        )}
      </div>

      <div className="md:col-span-4 flex md:justify-end items-start gap-2 flex-wrap">
        {STATUSES.filter((s) => s !== b.status).map((s) => (
          <button
            key={s}
            onClick={() => onUpdateStatus(b.id, s)}
            data-testid={`admin-set-${s}-${b.id}`}
            className="text-xs tracking-[0.16em] uppercase px-4 py-2.5 border border-[#6B4A2B]/25 text-[#5C5042] hover:bg-[#2C241B] hover:text-[#F9F6F0] hover:border-[#2C241B] transition-colors"
          >
            Mark {s}
          </button>
        ))}
      </div>
    </article>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="overline text-[#5C5042]">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}
